import { ReactNode } from "react";

import { Skeleton } from "@mui/material";
import { useLegend } from "@mui/x-charts";

import { ToggleButtons } from "../buttons";
import { ConfigOption, TimeDomainProps } from "./types";
import { useAppPalette } from "../../theme";
import { Row } from "../containers";
import { ChartsLabel } from "./ChartsLabel";
import { DEFAULT_TIME_DOMAIN_PERIODS } from "./constants";

type ChartsHeaderProps<T extends { date: number }> = {
    loading?: boolean;
    hide?: boolean;
    hideLegend?: boolean;
    config?: number;
    setConfig?: (i: number) => void;
    configOptions?: ConfigOption<T>[];
    components?: ReactNode;
} & TimeDomainProps;

export default function ChartsHeader<T extends { date: number }>({
    loading,
    hide,
    hideLegend,
    components,
    ...props
}: ChartsHeaderProps<T>) {
    if (hide) return null;
    if (loading) {
        return (
            <Row spaceBetween>
                <Skeleton width={80} />
                <Skeleton width={80} />
            </Row>
        );
    }
    return (
        <Row spaceBetween wrap sx={{ minWidth: 0, gap: 1, alignItems: "flex-start" }}>
            {!hideLegend && <ChartsLegend />}
            {components}
            <ChartsToolbar {...props} />
        </Row>
    );
}

function ChartsToolbar<T extends { date: number }>({
    config,
    setConfig,
    configOptions,
    period,
    setPeriod,
    periodOptions = DEFAULT_TIME_DOMAIN_PERIODS
}: Omit<ChartsHeaderProps<T>, "legendOptions">) {
    const { textSecondary } = useAppPalette();
    const sx = { "& .MuiToggleButton-root": { color: textSecondary } };

    const configTabs = configOptions?.map((o, i) => ({ label: o.label, value: i }));
    const hideConfigTabs = !configTabs || config === undefined || !setConfig;

    const periodTabs = periodOptions?.map((o) => ({ label: o.label, value: o.seconds }));
    const hidePeriodTabs = !periodTabs || period === undefined || !setPeriod;

    return (
        <Row spaceBetween sx={{ flexShrink: 0 }}>
            {!hideConfigTabs && (
                <ToggleButtons
                    value={config}
                    setValue={setConfig}
                    options={configTabs}
                    size="xsmall"
                    containerSx={sx}
                />
            )}
            {!hidePeriodTabs && (
                <ToggleButtons
                    value={period}
                    setValue={setPeriod}
                    options={periodTabs}
                    size="xsmall"
                    containerSx={sx}
                />
            )}
        </Row>
    );
}

function ChartsLegend() {
    const { items } = useLegend();
    return (
        <Row wrap sx={{ gap: 1, minWidth: 0, flex: "1 1 auto" }}>
            {items.map((i) => {
                const { color, seriesId, markType, label } = i;
                return <ChartsLabel key={seriesId} markType={markType} markColor={color} label={label} spacing={1} />;
            })}
        </Row>
    );
}
